/** @jsxImportSource @emotion/react */
import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@mui/material';
import { addClass, hasClass } from '@grapecity/wijmo';
import { CellType } from '@grapecity/wijmo.grid';
import {
  SearchBox,
  SearchBoxRow,
  InputBox,
  SearchRows,
  SearchCols,
  SearchButtonWrap,
} from 'components/layouts/SearchBox';
import CustomInputWithSearch from 'components/inputs/CustomInputWithSearch';
import { useMessageBar } from 'hooks/useMessageBar';
import { useCommonModal } from 'hooks/useCommonModal';
import SearchBoxButtons from 'components/buttons/SearchBoxButtons';
import { useLoading } from 'components/process/Loading';
import CustomGrid from 'components/grids/CustomGrid';
import {
  ControlBtnGroup,
  GlobalBtnGroup,
  SubTitleGroup,
  SubTitleLayout,
} from 'components/layouts/ContentLayout';
import { IconButton } from 'components/buttons/IconSVG';
import { GridStatusCellTemplate } from 'components/grids/GridStatusCellRenderer';
import { CommonUtil } from 'utils/CommonUtil';
import { findMdBldoList, saveMdBldoList } from 'apis/md/MdBldo';
import { MdBldo, MdBldoCondition } from 'models/md/MdBldo';
import { CrudCode } from 'models/common/Edit';
import { hasRole } from 'utils/SessionUtil';

/**
 * 동(빌딩) 조회 페이지
 */
const MdBldoInqPage = () => {
  const { t } = useTranslation();
  const { openLoading } = useLoading();
  const { openMessageBar } = useMessageBar();
  const { openCommonModal } = useCommonModal();

  const initCondition = {
    bldoNm: '',
  };

  const gridRef = useRef<any>();
  const [condition, setCondition] = useState<MdBldoCondition>({ ...initCondition });
  const [rowData, setRowData] = useState<MdBldo[]>([]);
  const [hasAuth, setHasAuth] = useState<boolean>(false);

  useEffect(() => {
    if (hasRole('MD_IVES_MANAGER') || hasRole('ADM')) {
      setHasAuth(true);
    }
  }, []);

  const layoutDefinition = useMemo(() => {
    return [
      {
        binding: 'crudKey',
        header: String(t('com.label.상태', '상태')),
        minWidth: 43,
        width: 43,
        align: 'center',
        isReadOnly: true,
        cellTemplate: GridStatusCellTemplate,
      },
      {
        binding: 'bldoId',
        header: String(t('com.label.동(빌딩) 코드', '동(빌딩) 코드')),
        width: 240,
        align: 'center',
      },
      {
        binding: 'bldoNm',
        header: String(t('com.label.동(빌딩) 명', '동(빌딩) 명')),
        width: '*',
        align: 'left',
      },
      {
        binding: 'dataUpdUserNm',
        header: String(t('com.label.수정자', '수정자')),
        width: 180,
        align: 'center',
      },
      {
        binding: 'dataUpdDtm',
        header: String(t('com.label.수정일시', '수정일시')),
        width: 180,
        align: 'center',
      },
    ];
  }, [rowData]);

  const initCodes = async () => {
    //
  };

  const onInitialized = (grid) => {
    gridRef.current = grid;

    grid.formatItem.addHandler((s, e) => {
      const item = e.panel.rows[e.row]?.dataItem;
      if (e.panel.cellType === CellType.TopLeft && e.cell.querySelector('input').indeterminate) {
        e.cell.querySelector('input').checked = true;
      }
      if (e.panel === s.rowHeaders) {
        if (e.cell.hasChildNodes()) {
          // 이미 사용중인 항목인 경우 선택불가/readonly
          if (item?.usedBldoYn === 'Y') {
            addClass(e.cell, 'wj-state-disabled');
          }
        }
      }
      if (CellType.Cell === e.panel.cellType) {
        const binding = e.panel.columns[e.col].binding;

        // 추가한 행의 bldoNm만 입력 UI 적용
        if (item.crudKey === CrudCode.CREATE && binding === 'bldoNm') {
          e.cell.ariaReadOnly = false;
        } else {
          e.cell.ariaReadOnly = true;
        }
      }
    });

    grid.hostElement.addEventListener('click', (e) => {
      // 전체 선택 시
      if (hasClass(e.target, 'wj-column-selector-group') && e.target.checked) {
        grid.deferUpdate(() => {
          grid.rows.forEach((row) => {
            if (row?.dataItem?.usedBldoYn === 'Y') {
              row.isSelected = false;
            }
          });
        });
      }
    });
  };

  const onBeginningEdit = (grid, e) => {
    const binding = grid.columns[e.col].binding;
    const item = grid.rows[e.row].dataItem;

    // 추가한 행의 bldoNm만 입력 가능
    if (item.crudKey === CrudCode.CREATE && binding === 'bldoNm') {
      e.cancel = false;
    } else {
      e.cancel = true;
    }
  };

  const handleOnChange = (name: string, value: any) => {
    setCondition((prev) => ({ ...prev, [name]: value }));
  };

  const btnSearch = () => {
    openLoading(true);
    findMdBldoList(condition)
      .then((res) => {
        setRowData(res);
      })
      .catch((err) => {
        openMessageBar({
          type: 'error',
          content: t(
            'com.msg.요청 정보 조회 중 오류가 발생했습니다.',
            '요청 정보 조회 중 오류가 발생했습니다.'
          ),
        });
      })
      .finally(() => openLoading(false));
  };

  const btnSave = () => {
    openCommonModal({
      modalType: 'confirm',
      content: t(`com.msg.변경사항을 저장하시겠습니까?`, `변경사항을 저장하시겠습니까?`),
      yesCallback: async () => {
        openLoading(true);
        saveMdBldoList(rowData)
          .then((res) => {
            if (res.successOrNot === 'Y') {
              openMessageBar({
                type: 'confirm',
                content: t('com.msg.저장 되었습니다.', '저장 되었습니다.'),
              });
              btnSearch();
            } else {
              openMessageBar({
                type: 'confirm',
                content: t('com.msg.저장에 실패하였습니다.', '저장에 실패하였습니다.'),
              });
            }
          })
          .catch((err) => {
            openMessageBar({
              type: 'error',
              content: t('com.msg.저장 중 오류가 발생했습니다.', '저장 중 오류가 발생했습니다.'),
            });
          })
          .finally(() => openLoading(false));
      },
    });
  };

  const btnAdd = () => {
    const newObj: MdBldo = {
      crudKey: CrudCode.CREATE,
      bldoNm: '',
    };
    setRowData((prev) => [newObj, ...prev]);
  };

  const btnDel = () => {
    const checkedIds: string[] = gridRef.current.rows
      .filter((row) => row.isSelected)
      .map((row) => row.dataItem)
      ?.map((o: MdBldo) => o.bldoId);
    if (checkedIds?.length === 0) {
      openMessageBar({
        type: 'error',
        content: t('com.msg.선택된 데이터가 없습니다.', '선택된 데이터가 없습니다.'),
      });
      return;
    }

    setRowData((prev) =>
      prev.map((o) => {
        if (checkedIds.includes(o.bldoId || '')) return { ...o, crudKey: CrudCode.DELETE };
        else return { ...o };
      })
    );
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      btnSearch();
    }
  };

  const handleDownloadExcel = () => {
    // excel download
    CommonUtil.exportWijmoExcelGrid(
      gridRef.current,
      t('md.label.동(빌딩) 목록', '동(빌딩) 목록'),
      t('md.label.동(빌딩) 목록', '동(빌딩) 목록')
    );
  };

  useEffect(() => {
    initCodes();
    btnSearch();
  }, []);

  return (
    <>
      <SearchBox>
        <SearchBoxRow>
          <InputBox>
            <SearchRows>
              <SearchCols>
                <label>{String(t('md.label.동(빌딩) 명', '동(빌딩) 명'))}</label>
                <CustomInputWithSearch
                  name="bldoNm"
                  value={condition.bldoNm}
                  placeholder={String(
                    t('md.label.동(빌딩) 명을 입력해 주세요.', '동(빌딩) 명을 입력해 주세요.')
                  )}
                  onChange={(e) => handleOnChange('bldoNm', e.target.value)}
                  onKeyDown={handleKeyDown}
                />
              </SearchCols>
            </SearchRows>
          </InputBox>
          <SearchButtonWrap>
            <SearchBoxButtons
              defaultCondition={initCondition}
              setReset={setCondition}
              onSearch={() => btnSearch()}
            />
          </SearchButtonWrap>
        </SearchBoxRow>
      </SearchBox>
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('md.label.동(빌딩) 목록', '동(빌딩) 목록')}</h3>
          <span className="total">
            {t('com.label.총', '총')}
            <span>{(rowData || []).length.toLocaleString()}</span>
            {t('com.label.건', '건')}
          </span>
        </SubTitleGroup>
        <ControlBtnGroup>
          {hasAuth && (
            <>
              <Button css={IconButton.button} className="addRow" onClick={btnAdd} disableRipple>
                {t('com.button.행추가', '행추가')}
              </Button>
              <Button css={IconButton.button} className="delRow" onClick={btnDel} disableRipple>
                {t('com.button.행삭제', '행삭제')}
              </Button>
            </>
          )}
          <Button
            css={IconButton.button}
            className="Exceldown"
            onClick={handleDownloadExcel}
            disableRipple
          >
            {t('com.button.다운로드', '다운로드')}
          </Button>
          <Button
            css={IconButton.button}
            className="refresh"
            onClick={() => btnSearch()}
            disableRipple
          >
            {t('com.button.새로고침', '새로고침')}
          </Button>
        </ControlBtnGroup>
      </SubTitleLayout>
      <CustomGrid
        layoutDefinition={layoutDefinition}
        rowData={rowData}
        initialized={onInitialized}
        beginningEdit={onBeginningEdit}
        height={500}
      />
      {hasAuth && (
        <GlobalBtnGroup>
          <Button css={IconButton.button} className="save" onClick={btnSave} disableRipple>
            {t('com.button.저장', '저장')}
          </Button>
        </GlobalBtnGroup>
      )}
    </>
  );
};

export default MdBldoInqPage;
